import { useEffect, useState, type ReactNode } from "react";
import { Button, Icon, Input, Segmented, formatDate } from "@omnabase/ui";
import { KEY_PROVIDERS, KEY_PROVIDER_LABELS, aiKeyMask, type AiKeyRead, type KeyCard, type KeyProvider } from "@omnabase/shared";
import { api } from "../../lib/api";
import { KEY_FIELD_HINTS, KEY_FIELD_NAMES, KEY_PLACEHOLDERS, keyPauseWarning, keyStateOf, type KeyStateView } from "../../lib/aiKey";
import { useSession } from "../../shell/store";
import { AiKeyModels } from "./AiKeyModels";

// Settings › AI › Engine, the workspace's own key (AI settings round two, 2026-10-19; round three: inside the "Your own key" card; round four,
// 2026-10-20: the stored-key row). The card's body, opened below its radio button: the provider row (Anthropic, since round six of
// 2026-10-21 Google Gemini, since round seven of 2026-10-22 OpenAI, all three choosable; the chip starts on the stored key's provider), then, while the key is chosen without a working key, the warning row (2026-10-21: "No key yet…" or "Your
// key stopped working: <reason>…"; the warning icon, never the check), then the stored-key row (a check mark on the accent for a working key, the warning icon for a failed one,
// the key's provider and the key as twelve dots and its last four, "set <date>", its state, "Check again" and "Remove key"), then the field:
// the chip's provider's key ("Anthropic API key", "Gemini API key", "OpenAI API key") before a key of that provider is stored, "Replace the key" after; the
// hint per provider. A key saved on the other provider's chip replaces the stored one. A pasted key is checked with one cheap call on "Check and save" (a refusal says why, nothing
// is stored and nothing is chosen); a working key is stored and becomes the engine in the same request. The key never comes back from the
// server; the field empties after a save. Under "Advanced", collapsed, the two model groups (`AiKeyModels.tsx`). A key stored while another
// engine runs shows its row in a frame joined below the "Your own key" card, beside (never inside) the card's button (`UnusedKeyFrame`).

type Busy = "save" | "check" | "remove" | null;
const PROVIDERS: { id: KeyProvider; label: string }[] = KEY_PROVIDERS.map((id) => ({ id, label: KEY_PROVIDER_LABELS[id] }));
/** the chip the card shows: the stored key's provider, following it after a save or a replace; Anthropic before any key */
function useChip(card: KeyCard | null): [KeyProvider, (c: KeyProvider) => void] {
  const [chip, setChip] = useState<KeyProvider>(card?.provider ?? "anthropic");
  useEffect(() => { if (card) setChip(card.provider); }, [card?.provider]); // eslint-disable-line react-hooks/exhaustive-deps
  return [chip, setChip];
}

/** the calls on the key with their busy state, the problem line and the toast; every call reloads the workspace, whose engine the radio shows */
function useKeyCalls(onRead: (r: AiKeyRead) => void) {
  const { workspace, reload, toast } = useSession();
  const url = `/api/v1/w/${workspace.slug}/ai/key`;
  const [problem, setProblem] = useState<string | null>(null); const [busy, setBusy] = useState<Busy>(null);
  const run = async (what: Busy, call: () => Promise<AiKeyRead>, done?: string): Promise<boolean> => {
    setBusy(what); setProblem(null);
    try { const r = await call(); onRead(r); await reload(); if (done) toast(done, { kind: "success" }); return true; }
    catch (e) { setProblem((e as Error).message); return false; }
    finally { setBusy(null); }
  };
  const remove = () => run("remove", () => api.del<AiKeyRead>(url), "Key removed");
  return { url, problem, busy, run, remove };
}

export function AiKeyCard({ read, chosen, onRead }: { read: AiKeyRead; chosen: boolean; onRead: (r: AiKeyRead) => void }) {
  const { url, problem, busy, run, remove } = useKeyCalls(onRead);
  const [value, setValue] = useState("");
  const card = read.key;
  const [chip, choose] = useChip(card);
  const fieldName = card?.provider === chip ? "Replace the key" : KEY_FIELD_NAMES[chip];
  const save = async () => { if (await run("save", () => api.put<AiKeyRead>(url, { provider: chip, key: value.trim(), use: true }), "Key checked and saved · your own key is the engine")) setValue(""); };
  return (
    <div className={`ob-choice-opt__panel${chosen ? " ob-choice-opt__panel--on" : ""}`}>
      <div className="ob-key-card__providers"><Segmented<KeyProvider> label="Provider" items={PROVIDERS} value={chip} onChange={choose} /></div>
      <PauseWarning text={keyPauseWarning(card, chosen)} />
      {card && (
        <StoredKeyRow card={card} state={keyStateOf(card, chosen ? "key" : read.engine)!}>
          <Button size="sm" busy={busy === "check"} onClick={() => void run("check", () => api.post<AiKeyRead>(`${url}/check`), "Key checked")}>Check again</Button>
          <Button size="sm" busy={busy === "remove"} onClick={() => void remove()}>Remove key</Button>
        </StoredKeyRow>
      )}
      <span className="ob-key-card__label">{fieldName}</span>
      <span className="ob-key-card__row">
        <Input className="ob-key-card__field" type="password" autoComplete="off" spellCheck={false} value={value} onChange={(e) => setValue(e.target.value)} placeholder={KEY_PLACEHOLDERS[chip]} aria-label={fieldName} />
        <Button variant="primary" size="sm" busy={busy === "save"} disabled={!value.trim()} onClick={() => void save()}>Check and save</Button>
      </span>
      <p className="ob-form-row__help">{KEY_FIELD_HINTS[chip].map((hint, i) => <span key={hint}>{i > 0 && <br />}{hint}</span>)}</p>
      {problem && <p className="ob-form-row__warning" role="alert">{problem}</p>}
      {card && <AiKeyModels read={read} url={url} onRead={onRead} />}
    </div>
  );
}

/** A key stored while another engine runs, or on a plan without AI (round four, rule 4 and decision 4): its row in a frame joined below the
 *  "Your own key" card, with "Remove key", which removes the key without choosing it, and no check mark (it works for nobody right now). */
export function UnusedKeyFrame({ read, onRead }: { read: AiKeyRead; onRead: (r: AiKeyRead) => void }) {
  const { problem, busy, remove } = useKeyCalls(onRead);
  if (!read.key) return null;
  return (
    <div className="ob-choice-opt__panel ob-choice-opt__panel--foot">
      <StoredKeyRow card={read.key} state={keyStateOf(read.key, "none")!}>
        <Button size="sm" variant="ghost" busy={busy === "remove"} onClick={() => void remove()}>Remove key</Button>
      </StoredKeyRow>
      {problem && <p className="ob-form-row__warning" role="alert">{problem}</p>}
    </div>
  );
}

/** the stored key as one row, named for screen readers "Stored key ending abcd": the mark, the provider's key ("Google Gemini key"), the twelve dots and the last four (read aloud as
 *  "key ending in abcd"), the date it was set, its state in words, and the row's buttons on the right */
function StoredKeyRow({ card, state, children }: { card: KeyCard; state: KeyStateView; children: ReactNode }) {
  return (
    <div role="group" aria-label={`Stored key ending ${card.last4}`} className="ob-key-card__stored">
      <span className="ob-key-card__facts">
        <KeyMark kind={state.kind} />
        <span className="ob-key-card__provider">{KEY_PROVIDER_LABELS[card.provider]} key</span>
        <span className="ob-key-card__mask" role="img" aria-label={`key ending in ${card.last4}`}>{aiKeyMask(card.last4)}</span>
        <span className="ob-key-card__meta">set <time dateTime={card.setAt}>{formatDate(card.setAt)}</time></span>
        <span className={state.kind === "failed" ? "ob-form-row__warning" : "ob-key-card__meta"}>{state.text}{state.checkedAt && <> · checked <time dateTime={state.checkedAt}>{formatDate(state.checkedAt)}</time></>}</span>
      </span>
      <span className="ob-key-card__actions">{children}</span>
    </div>
  );
}

/** the paused key's warning row: the warning icon and the warning's words, a row of its own above the stored key and the field */
function PauseWarning({ text }: { text: string | null }) {
  if (!text) return null;
  return <div className="ob-key-card__warning" role="status"><Icon name="error" size="sm" /><span className="ob-form-row__warning">{text}</span></div>;
}

/** the check mark (the kit's check icon, the accent's text colour on the accent) for a working key that runs; the warning icon for a failed one */
function KeyMark({ kind }: { kind: KeyStateView["kind"] }) {
  if (kind === "working") return <span className="ob-key-card__mark"><Icon name="check" size="xs" /></span>;
  if (kind === "failed") return <span className="ob-key-card__mark ob-key-card__mark--warn"><Icon name="error" size="sm" /></span>;
  return null;
}
